/* eslint-disable no-console */
import { TWidget } from 'widgets/Widget';
import { TOptimizelyFeatureExperimentationInstance, getFeatureExperimentationInstance } from '../global/OptimizelyFeatureExperimentationInstance';

const SERVICE_CHANGE_EVENT = 'consentServiceChange';
const CONSENT_SERVICE_REQUEST_EVENT = 'consentServiceRequest';
const SERVICE_OPTIMIZELY = 'Optimizely';

/**
 * Base Optimizely implementation.
 *
 * @param Widget - Base widget for extending.
 * @returns Optimizely class.
 */
function OptimizelyMixinClassCreator(Widget: TWidget) {
    /**
     * @class OptimizelyMixin
     * @classdesc Optimizely widget mixin for pickup point selection and address handling.
     */
    class OptimizelyMixin extends Widget {
        isConsentGiven: boolean = false;

        prefs() {
            return {
                optimizelyFeatureFlagKey: '',
                ...super.prefs()
            };
        }

        init() {
            this.eventBus().on(SERVICE_CHANGE_EVENT, 'processServiceChange');
            this.eventBus().emit(CONSENT_SERVICE_REQUEST_EVENT, SERVICE_OPTIMIZELY);

            super.init();
        }

        processServiceChange(type, accepted) {
            if (type === SERVICE_OPTIMIZELY) {
                this.isConsentGiven = accepted;
            }
        }

        getConsentStatus() {
            return this.isConsentGiven;
        }

        /**
         * @description Creates an Optimizely Feature Experimentation instance and decides the variation for cart_to_checkout_registration flag
         * @returns Promise<void>
         */
        async decideFeatureFlagVariation(flagKey?: string): Promise<string> {
            const currentFlagKey = flagKey || this.prefs()?.optimizelyFeatureFlagKey;
            const isOptimizelyTrackingAllowed = this.getConsentStatus();
            let decision = 'off';

            if (!currentFlagKey?.length || !isOptimizelyTrackingAllowed) {
                return decision;
            }

            try {
                const optimizelyInstance: TOptimizelyFeatureExperimentationInstance = getFeatureExperimentationInstance();

                decision = await optimizelyInstance?.decide(currentFlagKey);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to decide feature flag variation:\n', error);
            }

            return decision;
        }
    }

    return OptimizelyMixin;
}

export type TOptimizelyMixin = ReturnType<typeof OptimizelyMixinClassCreator>;

export type TOptimizelyMixinInstance = InstanceType<TOptimizelyMixin>;

export default OptimizelyMixinClassCreator;
